import { Box, Button, Card, CircularProgress, Grid, Typography, TextField, } from '@material-ui/core';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { isEmpty, isUndefined } from 'lodash';
import { ColorPicker, useColor } from 'react-color-palette';
import "react-color-palette/css";
import toast from 'react-hot-toast';
import { useOutletContext, useParams } from 'react-router-dom';
import { organizationApi } from 'src/api/organizationApi';
import { userApi } from 'src/api/userApi';
import useAuth from 'src/hooks/useAuth';
import logger from 'src/utils/logger';
import setTitle from 'src/utils/setTitle';


import { useFormik } from 'formik';
import * as Yup from 'yup';
import { margin } from '@material-ui/system';
import { casesApi } from 'src/api/casesApi';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'space-around',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center'
    },
  },
  form: {
    width: '100%',
    padding: '0px 40px',
  },
  projLimit: {
    padding: '0px 40px',
    '& .MuiButton-root': {
      margin: '10px !important'
    },
    '& .MuiFormControl-root': {
      margin: '10px !important'
    }
  },
  root: {
    borderRadius: '4px !important',
    paddingTop: '2.5rem',
    paddingBottom: '2.25rem',
    '& .rcp-dark[style]': {
      width: '90% !important',
      [theme.breakpoints.down('md')]: {
        width: '100% !important',
      },
    },
    '& .MuiTableCell-root': {
      paddingTop: 8.5,
      paddingBottom: 8.5,
    },
    '& .MuiToolbar-root': {
      minHeight: 60,
    },
    '& .css-15fev7r-MuiGrid-root': {
      display: 'block'
    },
    '& .MuiNativeSelect-select': {
      padding: '9.5px 14px',
      color: '#263238',
      fontWeight: 500,
      '& option': {
        color: '#263238',
        fontWeight: 400,
      },
    },
    '& .MuiTableRow-root': {
      cursor: 'pointer',
    },
  },
  card: {
    borderRadius: 5,
  },
  profileWrapper: {
    width: 215,
    margin: 'auto',
    backgroundColor: 'white',
    position: 'relative',
    '&:hover': {
      cursor: 'pointer',
      '& $editButton': {
        display: 'flex',
        width: '100%',
        height: '100%',
        color: '#121037',
        borderRadius: 0,
        backgroundColor: 'rgba(255, 255, 255, .75)',
        [theme.breakpoints.down('sm')]: {
          width: '50% !important',
        },
      },
      '& $deleteButton': {
        display: 'flex',
        color: '#DD0000',
        width: '100%',
        borderRadius: 0,
        backgroundColor: 'rgba(255, 255, 255, .75)',
        [theme.breakpoints.down('sm')]: {
          width: '50% !important',
        },
      },
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
      margin: 0,
    },
  },
  clientLogo: {
    width: 220,
    height: 120,
    margin: '0 auto',
    display: 'flex',
    border: '1px solid #f9f8f8',
    objectFit: 'contain',
    padding: '10px',
    [theme.breakpoints.down('md')]: {
      width: 253,
      height: 253,
    },
  },
  profileButtonWrapper: {
    position: 'absolute',
    top: 0,
    left: '0%',
    right: '50%',
    bottom: 0,
    height: 220,
    width: 220,
    opacity: 0,
    transition: '.5s ease',
    backgroundColor: 'rgba(255, 255, 255, .75)',
    '&:hover': {
      opacity: 0.5,
    },
  },
  editButton: {
    display: 'none',
  },
  deleteButton: {
    display: 'none',
  },
  buttonText: {
    marginLeft: 10,
    textTransform: 'capitalize',
    fontSize: '.8rem',
  },
  deleteText: {
    color: '#DD0000',
    fontSize: '.8rem',
  },
  logoText: {
    marginTop: '1rem',
    marginLeft: '4.1rem',
    marginBottom: 10,
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
    },
  },
  colorText: {
    marginTop: '1rem',
    marginBottom: 10,
    textAlign: 'left',
  },
  loadingWrapper: {
    marginTop: 20,
    textAlign: 'center',
  },
  loadingCircle: {
    color: theme.palette.primary.main,
    width: '20px !important',
    height: '20px !important',
    marginLeft: 5,
    marginRight: 5
  },
  switchWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginRight: 26,
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center',
    },
  },
  button: {
    width: 251,
    height: 45,
  },
  submitButton: {
    borderRadius: 4,
    textTransform: 'unset',
  },
}));

const ProjectLimit: FC = () => {
  const { organizationID } = useParams();


  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isColorSchemaSubmitting, setColorSchemaIsSubmitting] = useState<boolean>(false);
  const [data, setData] = useState<any>();
  const [initialData, setInitialData] = useState<any>();

  const classes = useStyles();
  const { user } = useAuth();


  const setOrgLimit = async (roleIdd, payload) => {
    try {
      const res = await casesApi.setOrgLimit(roleIdd, payload);
      toast.success('updated successfully');
      setData(res.data)
    } catch (error) {
      logger(error, 'error');
      toast.error('Failed to update');
    }
  };
  const getOrgLimit = async () => {
    try {
      const res = await casesApi.getOrgLimit(organizationID);
      // toast.success('updated successfully');
      setInitialData(res)
    } catch (error) {
      logger(error, 'error');
      toast.error('Failed get limit');
    }
  };

  // Formik setup
  const formik = useFormik({
    enableReinitialize: true, // Allow form to reinitialize with new values
    initialValues: {
      upperLimit: initialData?.reportingUpperLimit || '',
      lowerLimit: initialData?.reportingLowerLimit || '',
    },
    validationSchema: Yup.object({
      upperLimit: Yup.number()
        .required('Upper limit is required')
        .positive('Upper limit must be positive')
        .min(1, 'Must be greater than or equal to 1'),
      lowerLimit: Yup.number()
        .required('Lower limit is required')
        .min(0, 'Lower limit must be 0 or greater')
        .lessThan(
          Yup.ref('upperLimit'),
          'Lower limit must be less than upper limit'
        ),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      console.log('Form submitted:', values);
      const payload = {
        reportingUpperLimit: values.upperLimit,
        reportingLowerLimit: values.lowerLimit
      }
      const roleIdd = organizationID
      setOrgLimit(roleIdd, payload)

    },
  });

  useEffect(() => {
    getOrgLimit();
  }, []);



  return (

    <Card className={classes.root}>
      {/* <Typography variant="h5" gutterBottom>
        Update Project Limits
      </Typography> */}
      <Grid className={classes.wrapper}>

        <form onSubmit={formik.handleSubmit} noValidate className={classes.form}>
          <Box className='projLimit'>
            <TextField
              fullWidth
              id="upperLimit"
              name="upperLimit"
              label="Upper Limit"
              variant="outlined"
              value={formik.values.upperLimit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.upperLimit && Boolean(formik.errors.upperLimit)}
              helperText={formik.touched.upperLimit && formik.errors.upperLimit}
              sx={{
                margin: '10px 0px',
                width: '100% !important'
              }}
            />
            <TextField
              fullWidth
              id="lowerLimit"
              name="lowerLimit"
              label="Lower Limit"
              variant="outlined"
              value={formik.values.lowerLimit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lowerLimit && Boolean(formik.errors.lowerLimit)}
              helperText={formik.touched.lowerLimit && formik.errors.lowerLimit}
              sx={{
                margin: '10px 0px',
                width: '100%'
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              className={clsx(classes.submitButton)}
              // disabled={formik.isSubmitting}
              sx={{
                margin: '10px 0px',
              }}
            >
              Update Limit
              {/* {formik.isSubmitting && (
                <CircularProgress className={classes.loadingCircle} />
              )} */}
            </Button>
          </Box>
        </form>
      </Grid>
    </Card>
  );
};

export default ProjectLimit;
